import React from 'react';
import { useSiteContent } from '../context/SiteContentContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Award, CheckCircle } from 'lucide-react';
import CmsImage from '../components/CmsImage';

const certIcons = [
  <Award className="w-16 h-16 mx-auto text-[#e74c3c] mb-4" />,
  <CheckCircle className="w-16 h-16 mx-auto text-green-600 mb-4" />,
  <CheckCircle className="w-16 h-16 mx-auto text-green-600 mb-4" />
];

const About = () => {
  const { about: c } = useSiteContent();

  return (
    <div className="bg-white" data-testid="about-page">
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#003366] to-[#004080] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fadeInUp font-heading">{c.hero.title}</h1>
          <p className="text-xl max-w-3xl mx-auto animate-fadeInUp">{c.hero.subtext}</p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {c.mission.map((item, index) => (
              <div key={index} className="grid md:grid-cols-2 gap-12 items-center">
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <CmsImage src={item.image} alt={item.title} className="rounded-lg shadow-xl w-full h-[400px] object-cover" />
                </div>
                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <h2 className="text-4xl font-bold mb-6 text-[#003366] font-heading">{item.title}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4 text-[#003366] font-heading">{c.team.heading}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#e74c3c] to-[#c0392b] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{c.team.subtext}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {c.team.members.map((member, i) => (
              <Card key={i} className="hover-lift">
                <CardHeader>
                  <CmsImage src={member.image} alt={member.name} className="w-full h-64 object-cover rounded-t-lg mb-4" />
                  <CardTitle className="text-2xl text-gray-900">{member.name}</CardTitle>
                  <CardDescription className="text-lg text-[#e74c3c] font-medium">{member.role}</CardDescription>
                </CardHeader>
                <CardContent><p className="text-gray-700">{member.bio}</p></CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4 text-[#003366] font-heading">{c.certifications.heading}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#e74c3c] to-[#c0392b] mx-auto mb-6"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {c.certifications.items.map((it, i) => (
              <Card key={i} className="text-center hover-lift">
                <CardHeader>
                  {certIcons[i % certIcons.length]}
                  <CardTitle className="text-xl">{it.title}</CardTitle>
                </CardHeader>
                <CardContent><p className="text-gray-600">{it.description}</p></CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
